import React from "react";
import LegalLayout from "@/components/LegalLayout";

const EFFECTIVE_DATE = "August 22, 2026";

/**
 * TermsAndConditions — the agreement between the user and Essentialist
 * Dispatch (operator of N8One). Written in plain language. Reflects the
 * app's actual features (Becoming, Rituals, Journal, Capture, The
 * Collective, Pro subscription, wellness-practice consent flow).
 */
export default function TermsAndConditions() {
  return (
    <LegalLayout title="Terms & Conditions" effectiveDate={EFFECTIVE_DATE}>
      <section data-testid="terms-intro">
        <p>
          Welcome to N8One. These Terms &amp; Conditions (&ldquo;Terms&rdquo;) are the agreement
          between you and <strong>Essentialist Dispatch</strong>, the company that operates
          N8One (&ldquo;we,&rdquo; &ldquo;us,&rdquo; &ldquo;N8One&rdquo;). By creating an
          account or using the app, you agree to these Terms and to our{" "}
          <a href="/legal/privacy" className="underline">Privacy Policy</a>.
        </p>
        <p>
          We wrote this in ordinary language on purpose. If anything is unclear, email us at{" "}
          <a href="mailto:n8onelife@gmail.com" className="underline">n8onelife@gmail.com</a>.
        </p>
      </section>

      <section>
        <h2 className="font-display text-2xl">1. Eligibility &amp; your account</h2>
        <p>
          You must be at least 18 years old to use N8One. By creating an account you confirm you
          are 18 or older, that the information you give us is accurate, and that you will keep
          your Google sign-in secure. You are responsible for what happens on your account.
        </p>
        <p>
          One account per person. You may not share your account or let anyone else use it.
        </p>
      </section>

      <section>
        <h2 className="font-display text-2xl">2. What N8One is (and isn&rsquo;t)</h2>
        <p>
          N8One is a reflective wellness app for intentional aging. It includes:
        </p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li><strong>Becoming</strong> — Discover, Understand, Choose, and Capture flows with daily tracking and milestones.</li>
          <li><strong>Rituals</strong> — personal rituals and Wellness-Professional rituals you can adopt.</li>
          <li><strong>The Collective</strong> — a community feed of check-ins, wins, questions, and shared rituals.</li>
          <li><strong>Journal</strong> — private reflective writing.</li>
          <li><strong>Capture</strong> — a private photo library inside Becoming.</li>
          <li><strong>N8 Guide</strong> — an AI companion that offers reflections grounded in what you&rsquo;ve shared.</li>
          <li><strong>Pro subscription</strong> — optional paid features that go deeper on personalization.</li>
        </ul>
        <p className="mt-3 font-medium">
          N8One is <em>not</em> medical care. It is not a diagnosis, treatment, therapy, or
          substitute for a qualified healthcare professional. Nothing you read here is medical
          advice.
        </p>
      </section>

      <section>
        <h2 className="font-display text-2xl">3. Acceptable use</h2>
        <p>You agree not to:</p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li>Use N8One for anything illegal, harmful, or abusive.</li>
          <li>Impersonate anyone else, or misrepresent your credentials as a wellness professional.</li>
          <li>Post content that is harassing, discriminatory, hateful, sexually explicit, threatening, or that infringes anyone&rsquo;s rights.</li>
          <li>Post medical advice or make claims that a ritual, product, or practice will diagnose, treat, cure, or prevent any disease.</li>
          <li>Post content promoting unproven or exaggerated wellness claims, or content designed to pressure other members into purchases.</li>
          <li>Attempt to break, reverse-engineer, scrape, or overload the service.</li>
          <li>Upload photos of anyone who has not given you permission to share them.</li>
          <li>Use N8One to build a competing product or to train a machine-learning model.</li>
        </ul>
      </section>

      <section>
        <h2 className="font-display text-2xl">4. The Collective — community standards</h2>
        <p>
          The Collective is a shared space. By posting there, you agree to keep it real, respectful,
          and intentional:
        </p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li><strong>No hype.</strong> No exaggerated claims, promises, or pressure.</li>
          <li><strong>No solicitation.</strong> No selling, recruiting, promoting services, or seeking clients.</li>
          <li><strong>Share, don&rsquo;t sell.</strong> Share what has worked for you without turning your experience into an advertisement.</li>
          <li><strong>Support, don&rsquo;t compare.</strong> Everyone&rsquo;s journey is different. Encourage rather than judge.</li>
          <li><strong>Professionals.</strong> Wellness professionals are welcome to share knowledge and rituals, but may not use The Collective to solicit, promote services, recruit members, or seek clients.</li>
        </ul>
        <p className="mt-3">
          You can report any post, question, or reply that doesn&rsquo;t belong. Reports are
          reviewed by our admin moderation team. We may remove content, hide content, restrict
          features, suspend a professional&rsquo;s posting privileges, or terminate an account for
          violations of these standards or the acceptable-use rules above.
        </p>
      </section>

      <section>
        <h2 className="font-display text-2xl">5. Your content</h2>
        <p>
          You own the content you create in N8One — your journal entries, your tracking data,
          your rituals, your Capture photos, and everything you post to The Collective. We do
          not claim ownership of what you write or photograph.
        </p>
        <p>
          To operate the service, you grant us a limited, worldwide, royalty-free license to
          host, store, display, and (when you post to The Collective) show your content to other
          members. This license exists only so we can run N8One and ends when you delete the
          content or your account (subject to backups being overwritten in the ordinary course).
        </p>
        <p>
          <strong>Private content stays private.</strong> Journal entries, Capture photos, N8
          Guide conversations, Soul Compass answers, Intentional Self text, daily tracking,
          Becoming information, and procedure photos received from a wellness practice are
          visible only to you unless you intentionally share the content to The Collective.
        </p>
        <p>
          <strong>Public content stays public until you remove it.</strong> Content you post to
          The Collective — check-ins, before/after posts, questions, replies, reactions, shared
          rituals — is visible to other members while it is up. You can delete your own posts at
          any time.
        </p>
        <p>
          You are responsible for making sure you have the rights to post everything you upload,
          including photos of yourself and, where applicable, of anyone else pictured.
        </p>
      </section>

      <section>
        <h2 className="font-display text-2xl">6. Wellness professionals</h2>
        <p>
          If you use N8One as a wellness professional (including but not limited to spas,
          massage therapists, personal trainers, aestheticians, nutritionists, or coaches), you
          agree:
        </p>
        <ul className="list-disc pl-6 mt-2 space-y-1">
          <li>Everything you post is truthful about your identity, qualifications, and offerings.</li>
          <li>You will not use The Collective to solicit members, promote paid services, or attempt to acquire clients.</li>
          <li>You will only send Before/After procedure photos to a client after receiving that client&rsquo;s consent through the in-app consent flow, and you will stop sending them if the client revokes consent.</li>
          <li>You have obtained any releases required by law to share the images and information you post.</li>
        </ul>
        <p className="mt-3">
          We may hide, delete, or restrict shared rituals and professional posts that violate
          these terms, and we may remove professional status for repeated violations.
        </p>
      </section>

      <section>
        <h2 className="font-display text-2xl">7. Photos and intellectual property</h2>
        <p>
          Photos uploaded through Capture, check-ins, before/after posts, or the
          wellness-practice consent flow are stored in our object storage and are covered by the
          content license in Section 5. We do not use your photos to train AI models, sell them,
          or share them with third parties beyond the service providers listed in the Privacy
          Policy.
        </p>
        <p>
          Everything about the N8One app itself — the name, logos, design system, copy, brand
          voice, illustrations, code, and organization — belongs to Essentialist Dispatch and is
          protected by intellectual-property laws. You may not copy, reproduce, or reuse it
          without our written permission.
        </p>
      </section>

      <section>
        <h2 className="font-display text-2xl">8. N8 Guide (AI) &amp; its limitations</h2>
        <p>
          N8 Guide is powered by third-party AI models (currently Anthropic Claude). It is
          designed to offer reflective prompts, gentle summaries, and moderation assistance. It
          can be wrong. It can miss context. It can produce content that sounds confident but is
          incorrect. You should not treat N8 Guide as a doctor, therapist, lawyer, or financial
          adviser — because it isn&rsquo;t one.
        </p>
        <p>
          Use your own judgment for any decision that matters. If you have a health concern,
          talk to a qualified professional.
        </p>
      </section>

      <section>
        <h2 className="font-display text-2xl">9. Wellness &amp; educational disclaimer</h2>
        <p>
          Content in N8One — including rituals, reflections, tracking prompts, N8 Guide
          responses, Collective posts, shared rituals, and any information about supplements,
          peptides, procedures, or practices — is provided for reflective and educational
          purposes only. It is not medical care, not medical advice, and not a substitute for
          consultation with a qualified healthcare professional.
        </p>
        <p>
          Any decision you make based on information you read in N8One — including starting or
          stopping a supplement, undertaking a procedure, following a routine, or acting on
          another member&rsquo;s experience — is your responsibility.
        </p>
      </section>

      <section>
        <h2 className="font-display text-2xl">10. Pro subscription &amp; billing</h2>
        <p>
          N8One Pro is an optional paid subscription that unlocks deeper personalization
          features (as described in the app at the time of purchase). Payments are processed by
          Stripe, Inc. Prices, plans, and billing intervals are shown in the app when you
          subscribe.
        </p>
        <p>
          Your subscription renews automatically for the same interval and at the then-current
          price until you cancel. You can cancel at any time from your account; your Pro
          features remain active until the end of the current billing period, and no further
          charges will be made.
        </p>
        <p>
          <strong>All fees are non-refundable</strong> once paid, except where refunds are
          required by applicable law. Cancelling stops future billing but does not entitle you
          to a refund of the current period.
        </p>
        <p>
          If a payment fails, we may downgrade your account from Pro to Free until the payment
          is resolved.
        </p>
      </section>

      <section>
        <h2 className="font-display text-2xl">11. Suspension &amp; termination</h2>
        <p>
          We may suspend or terminate your access to N8One at any time if we reasonably believe
          you have violated these Terms, if your account is being used in a way that harms other
          members, or if we are required to by law. When possible we will tell you why and,
          where appropriate, give you a chance to resolve the issue.
        </p>
        <p>
          You can close your account at any time by emailing us. Sections that by their nature
          should survive termination (ownership, disclaimers, limitations of liability, dispute
          resolution) will survive.
        </p>
      </section>

      <section>
        <h2 className="font-display text-2xl">12. Third-party services</h2>
        <p>
          N8One relies on the third-party services listed in the Privacy Policy (Google, Stripe,
          Anthropic, ElevenLabs, Emergent, MongoDB). Their own terms govern how they operate.
          We are not responsible for their availability or their acts, but we choose them
          carefully and hold them to industry-standard practices.
        </p>
      </section>

      <section>
        <h2 className="font-display text-2xl">13. Disclaimers</h2>
        <p>
          N8One is provided <strong>&ldquo;as is&rdquo; and &ldquo;as available.&rdquo;</strong>{" "}
          To the maximum extent permitted by law, we disclaim all warranties, whether express or
          implied, including any warranty of merchantability, fitness for a particular purpose,
          non-infringement, accuracy, or uninterrupted operation. We do not warrant that the app
          will be error-free, that data will never be lost, or that any particular outcome will
          result from using it.
        </p>
      </section>

      <section>
        <h2 className="font-display text-2xl">14. Limitation of liability</h2>
        <p>
          To the maximum extent permitted by law, Essentialist Dispatch and its officers,
          employees, and service providers will not be liable for any indirect, incidental,
          consequential, special, punitive, or exemplary damages, or for lost profits, revenues,
          data, or goodwill, arising out of or related to your use of N8One — even if we&rsquo;ve
          been advised of the possibility.
        </p>
        <p>
          For all claims taken together, our total liability to you will not exceed the greater
          of (a) the amount you paid us for N8One Pro during the twelve months before the
          claim, or (b) one hundred US dollars ($100).
        </p>
        <p>
          Some jurisdictions do not allow the exclusion of certain warranties or limitations of
          liability, so some of the above may not apply to you.
        </p>
      </section>

      <section>
        <h2 className="font-display text-2xl">15. Indemnification</h2>
        <p>
          You agree to defend and hold Essentialist Dispatch harmless from claims, damages, and
          expenses (including reasonable attorney fees) arising from content you post, your
          violation of these Terms, or your violation of anyone else&rsquo;s rights.
        </p>
      </section>

      <section>
        <h2 className="font-display text-2xl">16. Changes to the app and to these Terms</h2>
        <p>
          We may add, change, or remove features at any time. If we make a material change to
          these Terms, we will update the &ldquo;Effective&rdquo; date at the top and, where
          appropriate, tell you in-app or by email before the change takes effect. Your
          continued use of N8One after the change takes effect means you accept the updated
          Terms.
        </p>
      </section>

      <section>
        <h2 className="font-display text-2xl">17. Governing law &amp; disputes</h2>
        <p>
          These Terms are governed by the laws of the State of Oregon, United States, without
          regard to conflict-of-laws principles. Any dispute that isn&rsquo;t resolved by good-
          faith conversation will be brought exclusively in the state or federal courts located
          in Jackson County, Oregon, and you consent to the personal jurisdiction of those
          courts. If you are a consumer in a jurisdiction that grants you protective rights that
          cannot be waived, nothing in this Section takes those rights away.
        </p>
      </section>

      <section>
        <h2 className="font-display text-2xl">18. Miscellaneous</h2>
        <p>
          These Terms, together with the Privacy Policy, are the entire agreement between you
          and us about N8One. If a court finds any part of these Terms unenforceable, the rest
          stays in force. Our failure to enforce a right is not a waiver of that right. You may
          not assign these Terms; we may assign them (for example, in connection with a merger
          or sale of the business).
        </p>
      </section>

      <section>
        <h2 className="font-display text-2xl">19. Contact</h2>
        <p>
          Questions about these Terms:
        </p>
        <p className="mt-2">
          Essentialist Dispatch<br />
          1432 Jackson Rd<br />
          Ashland, OR 97520<br />
          <a href="mailto:n8onelife@gmail.com" className="underline">n8onelife@gmail.com</a>
        </p>
      </section>
    </LegalLayout>
  );
}
